import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera } from "lucide-react";

export default function ProfilePage() {
  const { user, refetch } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
  }, [user]);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const avatarUrl = user?.avatar_path && token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${user.avatar_path}?auth=${encodeURIComponent(token)}`
    : "";

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f);
    const r = new FileReader(); r.onload = () => setPreview(r.result); r.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    if (newPwd && newPwd.length < 6) return toast.error("New password must be at least 6 characters");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("email", email);
      fd.append("phone", phone);
      if (newPwd) { fd.append("current_password", curPwd); fd.append("new_password", newPwd); }
      if (file) fd.append("avatar", file);
      await api.put("/users/me/profile", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile updated");
      setCurPwd(""); setNewPwd(""); setFile(null); setPreview("");
      await refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const displayAvatar = preview || avatarUrl;

  return (
    <div className="max-w-2xl space-y-8" data-testid="profile-page">
      <div>
        <div className="overline">Account</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Profile</h1>
      </div>

      <form onSubmit={save} className="grid-border-card p-6 space-y-6">
        <div className="flex items-center gap-5">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="relative w-24 h-24 rounded-full bg-secondary overflow-hidden flex items-center justify-center text-3xl font-display group"
            data-testid="profile-avatar-btn"
          >
            {displayAvatar
              ? <img src={displayAvatar} alt="avatar" className="w-full h-full object-cover"/>
              : <span>{user?.name?.[0]?.toUpperCase() || "?"}</span>}
            <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" strokeWidth={1.5}/>
            </span>
          </button>
          <div>
            <div className="font-medium">{user?.name}</div>
            <div className="text-xs text-muted-foreground">{user?.role === "admin" ? "Administrator" : "Store Manager"}</div>
            <button type="button" onClick={() => fileRef.current?.click()} className="text-xs uppercase tracking-widest text-accent hover:underline mt-2" data-testid="profile-change-pic">
              Change picture
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-avatar-input"/>
          </div>
        </div>

        <div className="grid gap-4">
          <div><Label>Name</Label><Input value={name} onChange={e=>setName(e.target.value)} data-testid="profile-name"/></div>
          <div><Label>Email</Label><Input type="email" value={email} onChange={e=>setEmail(e.target.value)} data-testid="profile-email"/></div>
          <div><Label>Phone</Label><Input value={phone} onChange={e=>setPhone(e.target.value)} data-testid="profile-phone"/></div>
        </div>

        <div className="pt-5 border-t border-border space-y-3">
          <div className="overline">Change password</div>
          <div><Label>Current password</Label><Input type="password" value={curPwd} onChange={e=>setCurPwd(e.target.value)} data-testid="profile-cur-pwd"/></div>
          <div><Label>New password</Label><Input type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} minLength={6} data-testid="profile-new-pwd"/></div>
          <p className="text-xs text-muted-foreground">Leave blank to keep your current password.</p>
        </div>

        <Button type="submit" disabled={saving} data-testid="profile-save">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </form>
    </div>
  );
}
